import { Component } from '@verndale/core';
import Chart from 'chart.js';

export const colors = {
  orange: {
    normal: '#F78842',
    light: '#FFDEC5'
  },
  blue: {
    normal: '#06A7DE',
    light: '#C9E5F7'
  },
  green: {
    normal: '#019A4B',
    light: '#C0DDC7'
  },
  purple: {
    normal: '#6F2981',
    light: '#CBBDD7'
  },
  pink: {
    normal: '#B94096',
    light: '#F0C7E0'
  }
};

class StatsGraph extends Component {
  setupDefaults() {
    this.dom = {
      $canvas: this.el._$('.stats__canvas'),
      $canvasContext: this.el._$('.stats__canvas').getContext('2d')
    };

    this.chartColor = this.el.dataset.chartColor;
    this.chartType = this.el.dataset.chartType;
    this.chartValue = this.el.dataset.chartValue;
    this.chartLabels = this.el.dataset.chartLabels;
    this.chart = null;

    this.observer = new window.IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.chart) {
            setTimeout(::this.initGraph, 500);
          }
        });
      },
      {
        threshold: 1
      }
    );
    this.observer.observe(this.dom.$canvas);
  }

  getData() {
    const BAR_THICKNESS = 20;
    const MAX_VALUE = 100;
    const [value1, value2] = this.chartValue.split(',').map(Number);
    const value = Number(this.chartValue);

    if (this.chartType === 'double-bar') {
      const [
        [color1, variation1],
        [color2, variation2]
      ] = this.chartColor.split(',').map((color) => {
        const [c, variation] = color.trim().split('.');
        return [c, variation];
      });

      return [
        {
          data: [value1, value2],
          backgroundColor: [
            colors[color1][variation1],
            colors[color2][variation2]
          ],
          maxBarThickness: BAR_THICKNESS
        }
      ];
    }

    if (this.chartType === 'bar') {
      return [
        {
          data: [value],
          backgroundColor: colors[this.chartColor].normal,
          maxBarThickness: BAR_THICKNESS
        },
        {
          data: [MAX_VALUE - value],
          backgroundColor: colors[this.chartColor].light,
          maxBarThickness: BAR_THICKNESS
        }
      ];
    }

    return [
      {
        data: [value, MAX_VALUE - value],
        backgroundColor: [
          colors[this.chartColor].normal,
          colors[this.chartColor].light
        ]
      }
    ];
  }

  getScales() {
    const fonts = {
      fontSize: 12,
      fontFamily: 'Atlas Grotesk'
    };

    if (this.chartType === 'bar' || this.chartType === 'double-bar') {
      return {
        scales: {
          xAxes: [
            {
              stacked: true,
              gridLines: {
                lineWidth: 1
              },
              ticks: {
                ...fonts
              }
            }
          ],
          yAxes: [
            {
              stacked: true,
              gridLines: {
                lineWidth: 1,
                offsetGridLines: false,
                drawBorder: false
              },
              ticks: {
                beginAtZero: true,
                maxTicksLimit: 2,
                display: this.chartType === 'double-bar' ? false : true,
                ...fonts
              }
            }
          ]
        }
      };
    }

    return {};
  }

  getLabels() {
    if (this.chartType === 'double-bar') {
      return this.chartLabels.split(',').map((label) => label.trim());
    }

    return [];
  }

  getType() {
    if (this.chartType === 'double-bar') {
      return 'bar';
    }

    return this.chartType;
  }

  initGraph() {
    const { $canvasContext } = this.dom;

    this.chart = new Chart($canvasContext, {
      type: this.getType(),
      data: {
        labels: this.getLabels(),
        datasets: this.getData()
      },
      options: {
        animation: {
          duration: 2000
        },
        responsive: false,
        scaleLabel: {
          display: false
        },
        hover: {
          mode: null
        },
        aspectRatio: 1,
        legend: {
          display: false
        },
        tooltips: {
          enabled: false
        },
        yAxes: [
          {
            type: 'linear',
            position: 'left'
          }
        ],
        ...this.getScales()
      }
    });
  }
}

export default StatsGraph;
